import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAdmin } from '../../hooks/useAdmin'
import { supabase } from '../../services/supabase'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../utils/constants'
import CompanyUsersPanel from './CompanyUsersPanel'

type SignMethod = 'google' | 'microsoft' | 'saml'
type WizardStepId = 'company' | 'method' | 'connect' | 'users'

type CompanySsoRow = {
  id: string
  name: string
  domain: string | null
  status: string
  sso_provider_id: string | null
  sso_domains: string[] | null
  sso_connected_at: string | null
  sso_it_contact: string | null
  preferred_sign_in?: SignMethod | null
  sso_auto_provision?: boolean
  member_count?: number
}

type SpInfo = {
  metadataUrl: string
  metadataDownloadUrl: string
  acsUrl: string
}

type AzureTenantUrlMode = 'organizations' | 'tenant' | 'missing'

type PlatformAuthStatus = {
  callbackUrl: string
  google: { enabled: boolean; clientIdSet: boolean }
  azure: {
    enabled: boolean
    clientIdSet: boolean
    tenantUrlSet: boolean
    tenantUrl: string | null
    tenantUrlMode: AzureTenantUrlMode
  }
  managementConfigured: boolean
  error?: string
}

const AZURE_ORGANIZATIONS_URL = 'https://login.microsoftonline.com/organizations'

async function callSsoAdmin(path: '' | string = '', init: RequestInit = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not signed in')
  const url = `${SUPABASE_URL}/functions/v1/sso-admin${path}`
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.error || json?.message || `HTTP ${res.status}`)
  }
  return json
}

function methodLabel(m: SignMethod | null | undefined): string {
  if (m === 'google') return 'Google'
  if (m === 'microsoft') return 'Microsoft'
  if (m === 'saml') return 'Company SSO'
  return 'Not chosen'
}

function Chip({
  ok,
  label,
  tone = 'neutral',
}: {
  ok?: boolean
  label: string
  tone?: 'neutral' | 'ok' | 'warn' | 'off'
}) {
  const cls =
    tone === 'ok' || ok === true
      ? 'bg-green-100 text-green-900 border-green-200'
      : tone === 'warn' || ok === false
        ? 'bg-amber-100 text-amber-900 border-amber-200'
        : tone === 'off'
          ? 'bg-gray-100 text-gray-600 border-gray-200'
          : 'bg-slate-100 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  )
}

function companyReady(
  row: CompanySsoRow | undefined,
  platformAuth: PlatformAuthStatus | null
): { ready: boolean; reasons: string[] } {
  const reasons: string[] = []
  if (!row) return { ready: false, reasons: ['No company selected'] }
  const method = row.preferred_sign_in
  if (!method) reasons.push('Sign-in method not chosen')
  const members = row.member_count || 0
  if (members < 1) reasons.push('No members yet')
  if (method === 'google' && !platformAuth?.google.enabled) reasons.push('Platform Google is Off')
  if (method === 'microsoft' && !platformAuth?.azure.enabled) reasons.push('Platform Microsoft is Off')
  if (method === 'saml' && !row.sso_provider_id) reasons.push('Company SSO not connected')
  return { ready: reasons.length === 0, reasons }
}

/**
 * Admin panel: Sign-in setup — platform Google/Microsoft once + per-customer onboarding.
 */
export default function SsoPanel() {
  const { companies, companiesLoading } = useAdmin()
  const [guideOpen, setGuideOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState<WizardStepId>('company')
  const [method, setMethod] = useState<SignMethod | ''>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<CompanySsoRow[]>([])
  const [sp, setSp] = useState<SpInfo | null>(null)
  const [platformAuth, setPlatformAuth] = useState<PlatformAuthStatus | null>(null)
  const [googleClientId, setGoogleClientId] = useState('')
  const [googleSecret, setGoogleSecret] = useState('')
  const [azureClientId, setAzureClientId] = useState('')
  const [azureSecret, setAzureSecret] = useState('')
  const [azureTenantUrl, setAzureTenantUrl] = useState('')
  const [savingPlatform, setSavingPlatform] = useState<'google' | 'azure' | null>(null)

  const [companyId, setCompanyId] = useState('')
  const [domains, setDomains] = useState('')
  const [metadataUrl, setMetadataUrl] = useState('')
  const [metadataXml, setMetadataXml] = useState('')
  const [itContact, setItContact] = useState('')
  const [success, setSuccess] = useState<string | null>(null)

  const applyPlatformAuth = (next: PlatformAuthStatus | undefined) => {
    if (!next) return
    setPlatformAuth(next)
    if (next.azure.tenantUrl) setAzureTenantUrl(next.azure.tenantUrl)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await callSsoAdmin('', { method: 'GET' })
      setRows((data.companies as CompanySsoRow[]) || [])
      setSp(data.sp || null)
      if (data.platformAuth) applyPlatformAuth(data.platformAuth as PlatformAuthStatus)
      if (data.samlError) setError(String(data.samlError))
      setCompanyId((prev) => prev || data.companies?.[0]?.id || '')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
      setSp({
        metadataUrl: `${SUPABASE_URL}/auth/v1/sso/saml/metadata`,
        metadataDownloadUrl: `${SUPABASE_URL}/auth/v1/sso/saml/metadata?download=true`,
        acsUrl: `${SUPABASE_URL}/auth/v1/sso/saml/acs`,
      })
      if (companies?.length) {
        setRows(
          companies.map((c: { id: string; name: string; domain?: string; status?: string }) => ({
            id: c.id,
            name: c.name,
            domain: c.domain || null,
            status: c.status || 'active',
            sso_provider_id: null,
            sso_domains: null,
            sso_connected_at: null,
            sso_it_contact: null,
            preferred_sign_in: null,
            sso_auto_provision: false,
            member_count: 0,
          }))
        )
      }
    } finally {
      setLoading(false)
    }
  }, [companies])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!companyId && companies?.[0]?.id) setCompanyId(companies[0].id)
  }, [companies, companyId])

  // Sync wizard method from persisted preferred_sign_in when company changes
  useEffect(() => {
    const row = rows.find((r) => r.id === companyId)
    setMethod(row?.preferred_sign_in || '')
  }, [companyId, rows])

  const persistPreferredSignIn = async (preferred: SignMethod) => {
    if (!companyId) return
    await callSsoAdmin('', {
      method: 'POST',
      body: JSON.stringify({
        action: 'setPreferredSignIn',
        companyId,
        preferredSignIn: preferred,
      }),
    })
    setRows((prev) =>
      prev.map((r) => (r.id === companyId ? { ...r, preferred_sign_in: preferred } : r))
    )
  }

  const persistSsoAutoProvision = async (enabled: boolean) => {
    if (!companyId) return
    setError(null)
    setSuccess(null)
    try {
      const data = await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({
          action: 'setSsoAutoProvision',
          companyId,
          enabled,
        }),
      })
      const next =
        typeof data?.company?.sso_auto_provision === 'boolean'
          ? data.company.sso_auto_provision
          : enabled
      setRows((prev) =>
        prev.map((r) => (r.id === companyId ? { ...r, sso_auto_provision: next } : r))
      )
      setSuccess(
        next
          ? 'Auto-add after Company SSO is on for this customer.'
          : 'Auto-add after Company SSO is off — Add users manually.'
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update auto-provision setting')
    }
  }

  const selectMethod = async (id: SignMethod) => {
    setMethod(id)
    setSuccess(null)
    setError(null)
    try {
      await persistPreferredSignIn(id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save sign-in method')
    }
  }

  const connect = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    try {
      await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({
          action: 'connect',
          companyId,
          domains,
          metadataUrl: metadataUrl || undefined,
          metadataXml: metadataXml || undefined,
          itContact: itContact || undefined,
        }),
      })
      setSuccess('Company SSO connected. Ask their IT to test login with Company SSO on the sign-in screen.')
      setMetadataUrl('')
      setMetadataXml('')
      setWizardStep('users')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Connect failed')
    } finally {
      setLoading(false)
    }
  }

  const disconnect = async (id: string, name: string) => {
    if (
      !confirm(
        `Turn off company SSO for ${name}? Their employees will no longer be able to use Company SSO for this connection.`
      )
    ) {
      return
    }
    setLoading(true)
    setError(null)
    setSuccess(null)
    try {
      await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({ action: 'disconnect', companyId: id }),
      })
      setSuccess(`SSO disconnected for ${name}.`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed')
    } finally {
      setLoading(false)
    }
  }

  const saveGoogle = async () => {
    setSavingPlatform('google')
    setError(null)
    setSuccess(null)
    try {
      const data = await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({
          action: 'configureGoogle',
          clientId: googleClientId,
          secret: googleSecret || undefined,
        }),
      })
      applyPlatformAuth(data.platformAuth)
      setGoogleSecret('')
      setSuccess(
        'Google sign-in saved. Buttons are global for the whole app — still add this customer’s emails in Onboard a customer → Users.'
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save Google')
    } finally {
      setSavingPlatform(null)
    }
  }

  const saveAzure = async () => {
    setSavingPlatform('azure')
    setError(null)
    setSuccess(null)
    try {
      const data = await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({
          action: 'configureAzure',
          clientId: azureClientId,
          secret: azureSecret || undefined,
          tenantUrl: azureTenantUrl,
        }),
      })
      applyPlatformAuth(data.platformAuth)
      setAzureSecret('')
      setSuccess(
        'Microsoft sign-in saved. Buttons are global for the whole app — still add this customer’s emails in Onboard a customer → Users.'
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save Microsoft')
    } finally {
      setSavingPlatform(null)
    }
  }

  const disableProvider = async (which: 'google' | 'azure') => {
    setSavingPlatform(which)
    setError(null)
    setSuccess(null)
    try {
      const data = await callSsoAdmin('', {
        method: 'POST',
        body: JSON.stringify({ action: which === 'google' ? 'disableGoogle' : 'disableAzure' }),
      })
      applyPlatformAuth(data.platformAuth)
      setSuccess(which === 'google' ? 'Google sign-in turned off.' : 'Microsoft sign-in turned off.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disable')
    } finally {
      setSavingPlatform(null)
    }
  }

  const copy = (text: string) => void navigator.clipboard.writeText(text)

  const connected = rows.filter((r) => r.sso_provider_id)
  const selectedRow = rows.find((r) => r.id === companyId)
  const selectedCompany =
    selectedRow || companies.find((c: { id: string }) => c.id === companyId)
  const selectedName = selectedCompany?.name || 'this company'
  const samlConnected = Boolean(selectedRow?.sso_provider_id)
  const effectiveMethod: SignMethod | '' =
    method || selectedRow?.preferred_sign_in || ''

  const stepDefs = useMemo(() => {
    if (effectiveMethod === 'saml') {
      return [
        { id: 'company' as const, label: 'Company' },
        { id: 'method' as const, label: 'Sign-in method' },
        { id: 'connect' as const, label: 'Connect' },
        { id: 'users' as const, label: 'Users + tell them' },
      ]
    }
    return [
      { id: 'company' as const, label: 'Company' },
      { id: 'method' as const, label: 'Sign-in method' },
      { id: 'users' as const, label: 'Users + tell them' },
    ]
  }, [effectiveMethod])

  useEffect(() => {
    if (wizardStep === 'connect' && effectiveMethod !== 'saml') {
      setWizardStep('users')
    }
  }, [effectiveMethod, wizardStep])

  const customerInstructions = (() => {
    if (!companyId || !effectiveMethod) return ''
    const origin =
      typeof window !== 'undefined' ? window.location.origin : 'the Early Signals app'
    if (effectiveMethod === 'google') {
      return `Sign in at ${origin} → click Google → use your work Google account. You must be added to ${selectedName} by The Simple Intel.`
    }
    if (effectiveMethod === 'microsoft') {
      return `Sign in at ${origin} → click Microsoft → use your work Microsoft account. You must be added to ${selectedName} by The Simple Intel.`
    }
    return `Sign in at ${origin} → click Company SSO → enter your work email. You must be added to ${selectedName} by The Simple Intel.`
  })()

  const canAdvanceFromMethod = Boolean(companyId && effectiveMethod)
  const canAdvanceFromConnect = effectiveMethod !== 'saml' || samlConnected
  const callbackUrl =
    platformAuth?.callbackUrl || `${SUPABASE_URL.replace(/\/$/, '')}/auth/v1/callback`
  const googleOn = Boolean(platformAuth?.google.enabled)
  const azureOn = Boolean(platformAuth?.azure.enabled)
  const setupStatus = companyReady(selectedRow, platformAuth)

  const goNextFromMethod = () => {
    if (effectiveMethod === 'saml') setWizardStep('connect')
    else setWizardStep('users')
  }

  const selectCompanyInWizard = (id: string) => {
    setCompanyId(id)
    setSuccess(null)
    setWizardStep('company')
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Sign-in setup</h2>
        <p className="text-sm text-gray-600 mt-1">
          Platform Google / Microsoft credentials are configured{' '}
          <span className="font-medium">once for the app</span>. Membership and preferred method are{' '}
          <span className="font-medium">per customer</span>. Only{' '}
          <span className="font-medium">@thesimpleintel.com</span> admins do this.
        </p>
      </div>

      {(error || success) && (
        <div className="sticky top-0 z-10 space-y-2">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 shadow-sm">
              {error}
              <button type="button" className="ml-2 underline text-xs" onClick={() => setError(null)}>
                Dismiss
              </button>
            </div>
          )}
          {success && (
            <div className="rounded border border-green-200 bg-green-50 text-green-900 text-sm px-3 py-2 shadow-sm">
              {success}
              <button
                type="button"
                className="ml-2 underline text-xs"
                onClick={() => setSuccess(null)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      <section className="border rounded-lg p-4 space-y-4 bg-white">
        <div>
          <h3 className="font-semibold text-gray-900">1. Platform buttons (once)</h3>
          <p className="text-xs text-gray-600 mt-1">
            Turns on Google and Microsoft buttons for everyone. Still add each customer’s emails under
            Onboard a customer → Users. Create the OAuth apps in Google Cloud / Azure, then paste
            credentials here — you do not need the Supabase Auth dashboard.
          </p>
        </div>
        <ol className="list-decimal pl-5 text-xs text-gray-700 space-y-1">
          <li>Create an OAuth client in Google Cloud and/or an app in Azure (Entra).</li>
          <li>Paste this callback URL into that app, then copy Client ID and Secret here.</li>
          <li>Save. Try Sign in → Google or Microsoft.</li>
        </ol>
        <div className="text-sm">
          <span className="font-medium">Callback URL</span>
          <div className="mt-1 flex flex-wrap items-start gap-2">
            <code className="text-xs break-all bg-slate-50 border rounded px-2 py-1">{callbackUrl}</code>
            <button type="button" className="text-xs underline" onClick={() => copy(callbackUrl)}>
              Copy
            </button>
          </div>
        </div>
        {platformAuth && !platformAuth.managementConfigured && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {platformAuth.error ||
              'Set SB_MANAGEMENT_TOKEN on production edge secrets to configure Google/Microsoft from Admin.'}
          </p>
        )}
        {platformAuth?.managementConfigured && platformAuth.error && (
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">
            {platformAuth.error}
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border rounded p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">Google</h4>
              <div className="flex flex-wrap gap-1">
                <Chip
                  tone={googleOn ? 'ok' : 'off'}
                  label={googleOn ? 'On' : 'Off'}
                />
                <Chip
                  tone={platformAuth?.google.clientIdSet ? 'ok' : 'off'}
                  label={platformAuth?.google.clientIdSet ? 'Client ID set' : 'Client ID missing'}
                />
              </div>
            </div>
            <label className="block text-xs">
              Client ID
              <input
                className="block mt-1 border rounded px-2 py-1.5 w-full text-sm"
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
                placeholder="….apps.googleusercontent.com"
                autoComplete="off"
              />
            </label>
            <label className="block text-xs">
              Secret {platformAuth?.google.clientIdSet ? '(leave blank to keep existing)' : ''}
              <input
                type="password"
                className="block mt-1 border rounded px-2 py-1.5 w-full text-sm"
                value={googleSecret}
                onChange={(e) => setGoogleSecret(e.target.value)}
                placeholder="Client secret Value (not Client ID)"
                autoComplete="new-password"
              />
            </label>
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1">
              Paste the Client secret <span className="font-medium">Value</span> from Google Cloud — not
              the Client ID.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
                disabled={savingPlatform !== null || !googleClientId.trim()}
                onClick={() => void saveGoogle()}
              >
                {savingPlatform === 'google' ? 'Saving…' : 'Save Google'}
              </button>
              {googleOn && (
                <button
                  type="button"
                  className="text-sm underline text-red-700 disabled:opacity-50"
                  disabled={savingPlatform !== null}
                  onClick={() => void disableProvider('google')}
                >
                  Turn off
                </button>
              )}
            </div>
          </div>
          <div className="border rounded p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">Microsoft</h4>
              <div className="flex flex-wrap gap-1">
                <Chip tone={azureOn ? 'ok' : 'off'} label={azureOn ? 'On' : 'Off'} />
                <Chip
                  tone={platformAuth?.azure.clientIdSet ? 'ok' : 'off'}
                  label={platformAuth?.azure.clientIdSet ? 'Client ID set' : 'Client ID missing'}
                />
                <Chip
                  tone={
                    platformAuth?.azure.tenantUrlMode === 'organizations'
                      ? 'ok'
                      : platformAuth?.azure.tenantUrlMode === 'tenant'
                        ? 'warn'
                        : 'off'
                  }
                  label={
                    platformAuth?.azure.tenantUrlMode === 'organizations'
                      ? 'Any org (organizations)'
                      : platformAuth?.azure.tenantUrlMode === 'tenant'
                        ? 'Home tenant only'
                        : 'Tenant URL missing'
                  }
                />
              </div>
            </div>
            <label className="block text-xs">
              Client ID
              <input
                className="block mt-1 border rounded px-2 py-1.5 w-full text-sm"
                value={azureClientId}
                onChange={(e) => setAzureClientId(e.target.value)}
                placeholder="Azure application (client) ID"
                autoComplete="off"
              />
            </label>
            <label className="block text-xs">
              Secret {platformAuth?.azure.clientIdSet ? '(leave blank to keep existing)' : ''}
              <input
                type="password"
                className="block mt-1 border rounded px-2 py-1.5 w-full text-sm"
                value={azureSecret}
                onChange={(e) => setAzureSecret(e.target.value)}
                placeholder="Entra Client secrets → Value (not Secret ID)"
                autoComplete="new-password"
              />
            </label>
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1">
              Must be the secret <span className="font-medium">Value</span> from Entra Certificates &amp;
              secrets (shown once). Pasting the Secret ID causes login to bounce home logged out.
            </p>
            <label className="block text-xs">
              Azure tenant URL (required)
              <input
                className="block mt-1 border rounded px-2 py-1.5 w-full text-sm font-mono"
                value={azureTenantUrl}
                onChange={(e) => setAzureTenantUrl(e.target.value)}
                placeholder={AZURE_ORGANIZATIONS_URL}
                autoComplete="off"
              />
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                className="text-xs underline text-blue-800 disabled:opacity-50"
                disabled={savingPlatform !== null}
                onClick={() => setAzureTenantUrl(AZURE_ORGANIZATIONS_URL)}
              >
                Use organizations
              </button>
              {platformAuth?.azure.tenantUrlMode === 'tenant' && (
                <span className="text-[11px] text-amber-800">
                  Home-tenant URL: Simple works; other companies (e.g. Kenvue) get AADSTS50020.
                </span>
              )}
            </div>
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1">
              For customer Microsoft sign-in use{' '}
              <code className="text-[10px]">{AZURE_ORGANIZATIONS_URL}</code> (any org work account). A
              Directory tenant ID URL is home-tenant only. Never{' '}
              <code className="text-[10px]">/common</code> or trailing{' '}
              <code className="text-[10px]">/v2.0</code>. Entra app must be Multitenant.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
                disabled={
                  savingPlatform !== null || !azureClientId.trim() || !azureTenantUrl.trim()
                }
                onClick={() => void saveAzure()}
              >
                {savingPlatform === 'azure' ? 'Saving…' : 'Save Microsoft'}
              </button>
              {azureOn && (
                <button
                  type="button"
                  className="text-sm underline text-red-700 disabled:opacity-50"
                  disabled={savingPlatform !== null}
                  onClick={() => void disableProvider('azure')}
                >
                  Turn off
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="border rounded-lg p-4 space-y-4 bg-white">
        <div>
          <h3 className="font-semibold text-gray-900">2. Onboard a customer</h3>
          <p className="text-xs text-gray-600 mt-1">
            {effectiveMethod === 'saml'
              ? 'Four steps for Company SSO. Stay on this tab — add users in the Users step (no need to leave for Tenants).'
              : 'Three steps for Google or Microsoft. Stay on this tab — add users in the Users step.'}
          </p>
        </div>

        {/* Company setup status card */}
        {companyId && (
          <div className="border rounded-lg bg-slate-50 px-3 py-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-gray-900">{selectedName}</p>
              <Chip
                tone={setupStatus.ready ? 'ok' : 'warn'}
                label={setupStatus.ready ? 'Ready' : 'Not ready'}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Chip
                tone={selectedRow?.preferred_sign_in ? 'ok' : 'warn'}
                label={`Sign-in: ${methodLabel(selectedRow?.preferred_sign_in)}`}
              />
              {selectedRow?.preferred_sign_in === 'google' ||
              selectedRow?.preferred_sign_in === 'microsoft' ? (
                <Chip
                  tone={
                    (selectedRow.preferred_sign_in === 'google' && googleOn) ||
                    (selectedRow.preferred_sign_in === 'microsoft' && azureOn)
                      ? 'ok'
                      : 'warn'
                  }
                  label={
                    (selectedRow.preferred_sign_in === 'google' && googleOn) ||
                    (selectedRow.preferred_sign_in === 'microsoft' && azureOn)
                      ? 'Platform ready'
                      : 'Platform Off'
                  }
                />
              ) : selectedRow?.preferred_sign_in === 'saml' ? (
                <Chip tone="neutral" label="Platform N/A" />
              ) : null}
              <Chip
                tone={samlConnected ? 'ok' : selectedRow?.preferred_sign_in === 'saml' ? 'warn' : 'off'}
                label={samlConnected ? 'Company SSO connected' : 'Company SSO not connected'}
              />
              <Chip
                tone={(selectedRow?.member_count || 0) > 0 ? 'ok' : 'warn'}
                label={`${selectedRow?.member_count || 0} users`}
              />
              {effectiveMethod === 'saml' && (
                <Chip
                  tone={selectedRow?.sso_auto_provision ? 'ok' : 'off'}
                  label={
                    selectedRow?.sso_auto_provision
                      ? 'SSO auto-add on'
                      : 'SSO auto-add off'
                  }
                />
              )}
            </div>
            {!setupStatus.ready && setupStatus.reasons.length > 0 && (
              <p className="text-[11px] text-amber-900">{setupStatus.reasons.join(' · ')}</p>
            )}
          </div>
        )}

        {/* Company roster */}
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">SAML</th>
                <th className="px-3 py-2">Members</th>
                <th className="px-3 py-2">Ready</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = companyReady(r, platformAuth)
                return (
                  <tr
                    key={r.id}
                    className={`border-t cursor-pointer hover:bg-slate-50 ${
                      r.id === companyId ? 'bg-slate-100' : ''
                    }`}
                    onClick={() => selectCompanyInWizard(r.id)}
                  >
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-xs">{methodLabel(r.preferred_sign_in)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.sso_provider_id ? 'Connected' : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.member_count ?? 0}</td>
                    <td className="px-3 py-2">
                      <Chip tone={st.ready ? 'ok' : 'warn'} label={st.ready ? 'Ready' : 'Not ready'} />
                    </td>
                  </tr>
                )
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No companies yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ol className="flex flex-wrap gap-2 text-xs">
          {stepDefs.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setWizardStep(s.id)}
                className={`px-2.5 py-1 rounded border ${
                  wizardStep === s.id
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                {i + 1}. {s.label}
              </button>
            </li>
          ))}
        </ol>

        {wizardStep === 'company' && (
          <div className="space-y-3">
            <label className="block text-sm">
              Customer company
              <select
                className="block mt-1 border rounded px-2 py-1.5 min-w-[260px]"
                value={companyId}
                disabled={companiesLoading || loading}
                onChange={(e) => {
                  setCompanyId(e.target.value)
                  setSuccess(null)
                }}
              >
                {(rows.length ? rows : companies).map((c: { id: string; name: string }) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {'sso_provider_id' in c && (c as CompanySsoRow).sso_provider_id
                      ? ' (Company SSO connected)'
                      : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-gray-600">
              Create a new company under Tenants → Companies only if it is not in this list yet.
            </p>
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
              disabled={!companyId}
              onClick={() => setWizardStep('method')}
            >
              Next — sign-in method
            </button>
          </div>
        )}

        {wizardStep === 'method' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              How should <span className="font-medium">{selectedName}</span> sign in?
            </p>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['google', 'Google'],
                  ['microsoft', 'Microsoft'],
                  ['saml', 'Company SSO (Okta / Entra)'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => void selectMethod(id)}
                  className={`px-3 py-2 rounded border text-sm ${
                    effectiveMethod === id
                      ? 'bg-black text-white border-black'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {effectiveMethod === 'google' || effectiveMethod === 'microsoft' ? (
              <p className="text-xs text-gray-600">
                No per-customer connect step. Next: add their emails.{' '}
                {effectiveMethod === 'google'
                  ? googleOn
                    ? 'Platform Google sign-in is On.'
                    : 'Complete Platform buttons (Google) above first.'
                  : azureOn
                    ? 'Platform Microsoft sign-in is On.'
                    : 'Complete Platform buttons (Microsoft) above first.'}
              </p>
            ) : null}
            {effectiveMethod === 'saml' ? (
              <p className="text-xs text-gray-600">
                Next step collects their metadata and connects this company only.
              </p>
            ) : null}
            <div className="flex gap-2">
              <button
                type="button"
                className="text-sm underline"
                onClick={() => setWizardStep('company')}
              >
                Back
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
                disabled={!canAdvanceFromMethod}
                onClick={goNextFromMethod}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {wizardStep === 'connect' && effectiveMethod === 'saml' && (
          <div className="space-y-3">
            {samlConnected ? (
              <div className="space-y-3">
                <p className="text-sm text-green-800">
                  Company SSO is already connected for {selectedName}. Continue to add users, or
                  Disconnect under Advanced if you need to replace it.
                </p>
                <label className="flex items-start gap-2 text-sm text-gray-800 max-w-xl">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={Boolean(selectedRow?.sso_auto_provision)}
                    onChange={(e) => void persistSsoAutoProvision(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Auto-add users after successful Company SSO</span>
                    <span className="block text-xs text-gray-600 mt-0.5">
                      Verified SAML sign-ins for this company&apos;s domain get a member row
                      automatically (Early Signals). Entra assignment still controls who can sign
                      in. Password sign-up with the same domain does not join.
                    </span>
                  </span>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Send their IT our metadata + ACS (copy in Advanced or the guide), then paste what they
                  send back.
                </p>
                {sp && (
                  <p className="text-xs break-all text-gray-600">
                    Metadata: {sp.metadataUrl}{' '}
                    <button type="button" className="underline" onClick={() => copy(sp.metadataUrl)}>
                      Copy
                    </button>
                    <br />
                    ACS: {sp.acsUrl}{' '}
                    <button type="button" className="underline" onClick={() => copy(sp.acsUrl)}>
                      Copy
                    </button>
                  </p>
                )}
                <label className="text-sm block">
                  Their email domain(s)
                  <input
                    className="block mt-1 border rounded px-2 py-1.5 w-full max-w-md"
                    placeholder="acme.com"
                    value={domains}
                    onChange={(e) => setDomains(e.target.value)}
                  />
                </label>
                <label className="text-sm block">
                  IT contact (optional)
                  <input
                    className="block mt-1 border rounded px-2 py-1.5 w-full max-w-md"
                    placeholder="it@acme.com"
                    value={itContact}
                    onChange={(e) => setItContact(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  Their connection link (preferred)
                  <input
                    className="block mt-1 border rounded px-2 py-1.5 w-full"
                    placeholder="https://…/saml/metadata"
                    value={metadataUrl}
                    onChange={(e) => setMetadataUrl(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  Or paste their XML
                  <textarea
                    className="block mt-1 border rounded px-2 py-1.5 w-full font-mono text-xs"
                    rows={3}
                    placeholder="<EntityDescriptor …>"
                    value={metadataXml}
                    onChange={(e) => setMetadataXml(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={loading || !companyId}
                  onClick={() => void connect()}
                  className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
                >
                  Connect company SSO
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="text-sm underline"
                onClick={() => setWizardStep('method')}
              >
                Back
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
                disabled={!canAdvanceFromConnect}
                onClick={() => setWizardStep('users')}
              >
                Next — add users
              </button>
            </div>
          </div>
        )}

        {wizardStep === 'users' && companyId && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {selectedRow?.sso_auto_provision && effectiveMethod === 'saml' ? (
                <>
                  <span className="font-medium">{selectedName}</span> auto-adds members after
                  Company SSO. You can still Add emails below for role changes or to pre-stage
                  users.
                </>
              ) : (
                <>
                  Add work emails for <span className="font-medium">{selectedName}</span>. Without a
                  company membership row they can sign in but will not see tenant data.
                </>
              )}
            </p>
            {effectiveMethod === 'saml' && (
              <label className="flex items-start gap-2 text-sm text-gray-800 max-w-xl">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={Boolean(selectedRow?.sso_auto_provision)}
                  onChange={(e) => void persistSsoAutoProvision(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Auto-add users after successful Company SSO</span>
                  <span className="block text-xs text-gray-600 mt-0.5">
                    Opt-in only. Requires a connected SAML provider and matching work email domain.
                  </span>
                </span>
              </label>
            )}
            <CompanyUsersPanel initialCompanyId={companyId} embedded lockCompany />
            {customerInstructions && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">What to tell this customer</h4>
                <p className="text-sm text-gray-700 border rounded p-3 bg-slate-50">
                  {customerInstructions}
                </p>
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() => copy(customerInstructions)}
                >
                  Copy instructions
                </button>
              </div>
            )}
            <button
              type="button"
              className="text-sm underline"
              onClick={() =>
                setWizardStep(effectiveMethod === 'saml' ? 'connect' : 'method')
              }
            >
              Back
            </button>
          </div>
        )}
      </section>

      <section className="border rounded-lg bg-slate-50">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          onClick={() => setGuideOpen((o) => !o)}
          aria-expanded={guideOpen}
        >
          <span className="font-semibold text-gray-900">Admin guide — Sign-in setup</span>
          <span className="text-xs text-gray-500">{guideOpen ? 'Hide' : 'Show'}</span>
        </button>
        {guideOpen && (
          <div className="px-4 pb-5 space-y-6 text-sm text-gray-700 border-t border-slate-200 pt-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">Always start with the customer</h3>
              <p>
                Sign-in is set up <span className="font-medium">one customer (company) at a time</span>{' '}
                in the Onboard a customer wizard above.
              </p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Select the company (create under Tenants → Companies only if missing).</li>
                <li>Choose Google, Microsoft, or Company SSO — the choice is saved on the company.</li>
                <li>
                  For Company SSO, optionally enable <span className="font-medium">Auto-add users after
                  successful Company SSO</span> so Entra-assigned users get Early Signals without a
                  manual Add (Kenvue uses this).
                </li>
                <li>
                  For Company SSO only: complete the Connect step with their metadata.
                </li>
                <li>
                  In the <span className="font-medium">Users</span> step, add each work email (same as
                  Tenants → Company users). Then copy “What to tell this customer.”
                </li>
              </ol>
              <p className="text-xs text-gray-600">
                The status card and roster show method, SAML connection, member count, and Ready. Ready
                means method set, at least one member, platform On for Google/Microsoft, and SAML
                connected when Company SSO is chosen.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">Option A — Google</h3>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Platform buttons: Google On.</li>
                <li>Wizard: company → Google → Users (add emails).</li>
                <li>Tell them: Sign in → Google.</li>
              </ol>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">Option B — Microsoft</h3>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Platform buttons: Microsoft On (tenant URL without /v2.0).</li>
                <li>Wizard: company → Microsoft → Users.</li>
                <li>Tell them: Sign in → Microsoft.</li>
              </ol>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">Option C — Company SSO</h3>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Send their IT our metadata + ACS (Advanced section).</li>
                <li>Wizard: company → Company SSO → Connect → Users.</li>
                <li>Tell them: Sign in → Company SSO → work email.</li>
              </ol>
            </div>

            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Access is always per customer via company membership. Google/Microsoft buttons are
              configured once under Platform buttons (not per customer).
            </p>
          </div>
        )}
      </section>

      <section className="space-y-3 border rounded-lg p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between text-left"
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          <h3 className="font-semibold">Advanced — Connected companies &amp; SP URLs</h3>
          <span className="text-xs text-gray-500">{advancedOpen ? 'Hide' : 'Show'}</span>
        </button>
        {advancedOpen && (
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              Connect Company SSO only in the wizard Connect step. This section lists existing SAML
              connections and the SP URLs to send their IT.
            </p>
            {sp && (
              <div className="overflow-x-auto border rounded bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">What</th>
                      <th className="px-3 py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-3 py-2 font-medium whitespace-nowrap">Our metadata link</td>
                      <td className="px-3 py-2">
                        <code className="text-xs break-all">{sp.metadataUrl}</code>
                        <button
                          type="button"
                          className="ml-2 text-xs underline"
                          onClick={() => copy(sp.metadataUrl)}
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2 font-medium whitespace-nowrap">Our ACS link</td>
                      <td className="px-3 py-2">
                        <code className="text-xs break-all">{sp.acsUrl}</code>
                        <button
                          type="button"
                          className="ml-2 text-xs underline"
                          onClick={() => copy(sp.acsUrl)}
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Connected companies (SAML)</h4>
              <button
                type="button"
                className="text-sm underline"
                onClick={() => void load()}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Domains</th>
                    <th className="px-3 py-2">Connected</th>
                    <th className="px-3 py-2">IT contact</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {connected.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="px-3 py-2">{c.name}</td>
                      <td className="px-3 py-2 text-xs">{(c.sso_domains || []).join(', ') || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {c.sso_connected_at ? new Date(c.sso_connected_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">{c.sso_it_contact || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="text-xs underline text-red-700"
                          onClick={() => void disconnect(c.id, c.name)}
                        >
                          Disconnect
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!connected.length && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                        No company SSO connections yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
